import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/right-arrow.svg';
import getCurrentTimeInLocalISO from '../../shared-functions/CurrentTimeInLocalISO';

interface TimelineDataProperties {
  start: string;
  end: string;
  status: string;
  sizeInPX?: number;
  defaultEndDateUsed?: boolean;
}

interface TimelineLabelProperties {
  name: string;
  color: string;
  key: string;
}

interface CustomTimelineChartProperties {
  title?: string;
  timelineData: TimelineDataProperties[];
  timelineLabel: TimelineLabelProperties[];
  hourSizeInPX?: number;
  date: string; // Date in YYYY-MM-DD -> Used to merge same status and fill empty data in timeline data
  defaultStatusToShow?: string;
  dataLoading: boolean;
}

/**
 * Function to set a endsAtAdjusted date for the missing data
 * @param sessions
 * @returns
 */
function processTimelineData(
  sessions: TimelineDataProperties[],
  dayEnd: string,
): TimelineDataProperties[] {
  // Sort the session data based on createdAt (earliest first)
  const createdAtSorted = [...sessions].sort((a, b) => {
    return new Date(a.start).getTime() - new Date(b.start).getTime();
  });

  return createdAtSorted.map((session, index) => {
    // If endsAtAdjusted is null, we need to calculate it
    if (session.end === null) {
      let newEndsAt: string;

      // If this isn't the first session, use the previous session's startsAtAdjusted
      if (index === sessions.length - 1) {
        // Add current local time
        newEndsAt = getCurrentTimeInLocalISO();
      } else {
        newEndsAt = sessions[index + 1].start;
      }

      // Cap at 23:59:00Z if it exceeds the day end
      if (new Date(newEndsAt) > new Date(dayEnd)) {
        newEndsAt = dayEnd;
      }

      // Return a new object with the calculated endsAtAdjusted
      return {
        ...session,
        end: newEndsAt,
        defaultEndDateUsed: true, // Just to identify
      };
    }

    // If endsAtAdjusted is not null, return the session as-is
    return session;
  });
}

/**
 * Processes timeline data by merging overlapping 'online' periods,
 * filling gaps as 'hide-data' periods, and calculating sizeInPX for each period.
 * Ensures coverage from 00:00 to 23:59 of the specified date.
 *
 * @param data - Array of timeline data entries.
 * @param hourPX - Number of pixels representing one hour.
 * @param date - The date for which the timeline is processed (format: 'YYYY-MM-DD').
 * @returns Array of processed timeline data entries.
 */
function processTimelineDataProperties(
  data: TimelineDataProperties[],
  hourPX: number,
  date: string,
  defaultStatusToShow?: string,
): TimelineDataProperties[] {
  const minutesPerPX = 60 / hourPX;
  const dayStart = new Date(`${date}T00:00:00Z`);
  const dayEnd = new Date(`${date}T23:59:00Z`);

  // If no data, return a full day of hided-data
  if (data.length === 0) {
    return [
      {
        start: dayStart.toISOString(),
        end: dayEnd.toISOString(),
        status: defaultStatusToShow || `hided-data-${date}`,
        sizeInPX: Math.round((dayEnd.getTime() - dayStart.getTime()) / (60000 * minutesPerPX)),
      },
    ];
  }

  // Convert string timestamps to Date objects for processing
  const withDates = data.map((period) => ({
    ...period,
    startDate: new Date(period.start),
    endDate: new Date(period.end),
  }));

  // Filter and clip to our date range
  const normalizedData = withDates
    .filter((period) => period.endDate > dayStart && period.startDate < dayEnd)
    .map((period) => ({
      ...period,
      startDate: period.startDate < dayStart ? dayStart : period.startDate,
      endDate: period.endDate > dayEnd ? dayEnd : period.endDate,
    }));

  // Group by status
  const statusGroups: Record<string, typeof normalizedData> = {};
  normalizedData.forEach((period) => {
    if (!statusGroups[period.status]) {
      statusGroups[period.status] = [];
    }
    statusGroups[period.status].push(period);
  });

  // Merge each status group
  const mergedGroups: TimelineDataProperties[][] = Object.values(statusGroups).map((group) => {
    if (group.length === 0) return [];

    // Sort by start time
    const sorted = [...group].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const merged: TimelineDataProperties[] = [];

    /* eslint-disable no-restricted-syntax */
    for (const period of sorted) {
      const last = merged[merged.length - 1];
      if (last && period.startDate <= new Date(last.end)) {
        // Merge overlapping periods
        const lastEnd = new Date(last.end);
        last.end = new Date(Math.max(lastEnd.getTime(), period.endDate.getTime())).toISOString();
      } else {
        merged.push({
          start: period.startDate.toISOString(),
          end: period.endDate.toISOString(),
          status: period.status,
        });
      }
    }

    return merged;
  });

  // Combine all merged groups and sort by start time
  const allMergedPeriods = mergedGroups
    .flat()
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Fill gaps with hided-data periods
  const fullTimeline: TimelineDataProperties[] = [];
  let currentTime = dayStart;

  /* eslint-disable no-restricted-syntax */
  for (const period of allMergedPeriods) {
    const periodStart = new Date(period.start);
    if (periodStart > currentTime) {
      // Add hided-data period
      fullTimeline.push({
        start: currentTime.toISOString(),
        end: periodStart.toISOString(),
        status: defaultStatusToShow || `hided-data-${date}`,
        sizeInPX: Math.round(
          (periodStart.getTime() - currentTime.getTime()) / (60000 * minutesPerPX),
        ),
      });
    }

    // Add the current period
    const periodEnd = new Date(period.end);
    fullTimeline.push({
      ...period,
      sizeInPX: Math.round((periodEnd.getTime() - periodStart.getTime()) / (60000 * minutesPerPX)),
    });

    currentTime = periodEnd;
  }

  // Add final hided-data period if needed
  if (currentTime < dayEnd) {
    fullTimeline.push({
      start: currentTime.toISOString(),
      end: dayEnd.toISOString(),
      status: defaultStatusToShow || `hided-data-${date}`,
      sizeInPX: Math.round((dayEnd.getTime() - currentTime.getTime()) / (60000 * minutesPerPX)),
    });
  }

  return fullTimeline;
}

/**
 * Convert index to time
 * @param index
 * @returns
 */
function indexToTime(index: number) {
  const hour = index % 24;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;

  return `${displayHour}:00 ${period}`;
}

/**
 * Component to display a timeline graph for a single day
 * @returns
 */
function CustomTimelineChart(properties: CustomTimelineChartProperties) {
  const {
    timelineData,
    timelineLabel,
    title,
    hourSizeInPX,
    date,
    defaultStatusToShow,
    dataLoading = false,
  } = properties;
  const { i18n } = useTranslation();
  const [timelineDataToShow, setTimelineDataToShow] = useState<TimelineDataProperties[]>([]);
  const eachHourSizeInPX = hourSizeInPX && hourSizeInPX > 60 ? hourSizeInPX : 60; // If it less than, scss may affect, and there will time label will hide

  const scrollReference = useRef<HTMLDivElement | null>(null);
  const timeViewReference = useRef<HTMLDivElement | null>(null);
  const SCROLL_AMOUNT = 2 * eachHourSizeInPX; // scroll by 2 hours as step

  /**
   * Scroll functionality left
   */
  const scrollLeft = () => {
    if (scrollReference.current && timeViewReference.current) {
      scrollReference.current.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
      timeViewReference.current.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
    }
  };

  /**
   * Scroll functionality right
   */
  const scrollRight = () => {
    if (scrollReference.current && timeViewReference.current) {
      scrollReference.current.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
      timeViewReference.current.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
    }
  };

  /**
   * Function to find the color of the bar from legends
   * @param status
   * @returns
   */
  const getBackgroundColor = (status: string): string => {
    let bgColor = 'transparent';

    const foundLabel = timelineLabel.find((label) => label.key === status?.toLowerCase());

    if (foundLabel) {
      bgColor = foundLabel?.color;
    }

    return bgColor;
  };

  useEffect(() => {
    const processedTimeLineData = processTimelineData(timelineData, `${date}T23:59:00Z`);
    setTimelineDataToShow(
      processTimelineDataProperties(
        processedTimeLineData,
        eachHourSizeInPX,
        date,
        defaultStatusToShow,
      ),
    );

    // Reset scroll positions when data changes
    if (scrollReference.current && timeViewReference.current) {
      scrollReference.current.scrollTo({ left: 0, behavior: 'auto' });
      timeViewReference.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [timelineData]);

  return (
    <div className="custom-timeline-chart-wrap">
      <div className="custom-timeline-chart-header">
        <h3>{title}</h3>
        <div className="custom-timeline-chart-legend">
          {timelineLabel?.map((eachLabel, index) => (
            <div key={index} className="custom-timeline-chart-legend-item">
              <div
                className="custom-timeline-chart-legend-tag"
                style={{ backgroundColor: eachLabel.color }}
              />
              <span style={{ color: eachLabel.color }}>{eachLabel.name}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="custom-timeline-chart-container">
        <div
          className="arrow-container left"
          role="presentation"
          onClick={scrollLeft}
          style={{
            transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
          <LeftArrow />
        </div>
        {dataLoading ? (
          <div className="custom-timeline-chart-loading">
            <div className="skeleton" />
          </div>
        ) : (
          <div
            ref={scrollReference}
            className="custom-timeline-chart"
            style={
              {
                '--columnLength': timelineDataToShow.length,
              } as React.CSSProperties
            }>
            {timelineDataToShow
              ?.filter((eachData) => eachData?.sizeInPX !== 0)
              ?.map((eachData) => (
                <div
                  className="time-line-bar"
                  style={
                    {
                      width: `${eachData?.sizeInPX}px`,
                      // transition: 'all 0.4s ease-out', // Animation not working smoothly as expected
                      backgroundColor: getBackgroundColor(eachData?.status),
                    } as React.CSSProperties
                  }
                />
              ))}
          </div>
        )}
        <div
          className="arrow-container"
          role="presentation"
          onClick={scrollRight}
          style={{
            transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
          <RightArrow />
        </div>
      </div>
      <div className="custom-timeline-time-view" ref={timeViewReference}>
        <div
          className="custom-timeline-time-view-container"
          style={{
            width: `${24 * eachHourSizeInPX}px`,
          }}>
          {Array.from({ length: 24 }, (_, index) => (
            <div
              className="custom-timeline-chart-time-label"
              key={index}
              style={{
                left: `${index * eachHourSizeInPX}px`,
              }}>
              {indexToTime(index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CustomTimelineChart;
